import { HTMLAttributes, ReactNode } from 'react'

import Icon from '../../ui/Icon'
import { ListCardField, ListCardStripe } from '../../../types/listing'

type ListCardProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  title?: ReactNode
  meta?: ReactNode
  subtitle?: ReactNode
  status?: ReactNode
  menu?: ReactNode
  header?: ReactNode
  stripe?: ListCardStripe
  details?: ReactNode
  fields?: ListCardField[]
  actions?: ReactNode
  children?: ReactNode
  tone?: string
  muted?: boolean
  onOpen?: () => void
  openLabel?: string
}

export const ListCard = ({
  title,
  meta,
  subtitle,
  status,
  menu,
  header,
  stripe,
  details,
  fields,
  actions,
  children,
  tone = '',
  muted = false,
  onOpen,
  openLabel,
  className = '',
  ...props
}: ListCardProps) => {
  const hasHeading = title !== undefined || subtitle !== undefined
  const hasCorner = Boolean(status || menu || onOpen)
  const hasHeader = hasHeading || hasCorner || header

  const classes = [
    'ph-card list-item-card',
    onOpen ? 'list-item-card-clickable' : '',
    muted ? 'list-item-card-muted' : '',
    tone,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes} {...props}>
      {stripe && (
        <span
          className={`list-item-card-stripe is-${stripe}`}
          aria-hidden="true"
        />
      )}

      <div className="list-item-card-body">
        {hasHeader && (
          <div className="list-item-card-header">
            {hasHeading && (
              <div className="list-item-card-heading">
                {title !== undefined && (
                  <h3 className="list-item-card-title">
                    {title}
                    {meta !== undefined && (
                      <span className="list-item-card-meta mono muted">
                        {meta}
                      </span>
                    )}
                  </h3>
                )}
                {subtitle !== undefined && (
                  <div className="list-item-card-subtitle muted">
                    {subtitle}
                  </div>
                )}
              </div>
            )}

            {hasCorner && (
              <div className="list-item-card-corner">
                {status}
                {menu && <div className="list-item-card-menu">{menu}</div>}
                {onOpen && (
                  <button
                    type="button"
                    className="list-item-card-open stretched-link"
                    aria-label={openLabel}
                    title={openLabel}
                    onClick={onOpen}
                  >
                    <Icon name="chevR" size={16} />
                  </button>
                )}
              </div>
            )}

            {header && <div className="list-item-card-extra">{header}</div>}
          </div>
        )}

        {details && <div className="list-item-card-details">{details}</div>}

        {fields && fields.length > 0 && (
          <dl className="list-item-card-fields">
            {fields.map(({ key, label, value, mono = true, wide = false }) => (
              <div
                key={key}
                className={`list-item-card-field ${wide ? 'is-wide' : ''}`.trim()}
              >
                <dt className="muted">{label}</dt>
                <dd className={mono ? 'mono' : undefined}>{value}</dd>
              </div>
            ))}
          </dl>
        )}

        {children && <div className="list-item-card-content">{children}</div>}

        {actions && (
          <div className="row g-2 list-item-card-actions">{actions}</div>
        )}
      </div>
    </div>
  )
}
